import { createVuetify } from 'vuetify'
import { aliases, mdi } from 'vuetify/iconsets/mdi'
import { createVueI18nAdapter } from 'vuetify/locale/adapters/vue-i18n'
import { VSnackbarQueue } from 'vuetify/components/VSnackbarQueue'
import { createRulesPlugin } from 'vuetify/labs/rules'
import mdiCalendarBlank from '~icons/mdi/calendar-blank'
import i18n from './i18n'
import { useI18n } from 'vue-i18n'

// Mapped keys are limited to app keys that actually exist in the customer
// locale catalogs — unmapped Vuetify keys fall back to Vuetify's built-in
// translations (merged into $vuetify in plugins/i18n.ts).
const vuetifyLocaleKeyMap: Record<string, string> = {
  '$vuetify.dataFooter.nextPage': 'common.next',
  '$vuetify.close': 'common.close',
  '$vuetify.dismiss': 'common.close',
  '$vuetify.confirmEdit.ok': 'common.ok',
  '$vuetify.confirmEdit.cancel': 'common.cancel',
  '$vuetify.stepper.next': 'common.next',
  '$vuetify.pagination.ariaLabel.next': 'common.next',
  '$vuetify.rules.required': 'common.required',
}

/**
 * Creates a locale adapter that wraps the vue-i18n adapter and maps
 * Vuetify's internal locale keys to our application's keys.
 */
function createMappedVueI18nAdapter() {
  const baseAdapter = createVueI18nAdapter({ i18n, useI18n })

  return {
    ...baseAdapter,
    t: (key: string, ...params: unknown[]) => {
      // Check if this Vuetify key has a mapping to our app's keys
      const mappedKey = vuetifyLocaleKeyMap[key]
      if (mappedKey) {
        // Use our app's key instead, handle params properly
        if (params.length > 0) {
          return i18n.global.t(mappedKey, params)
        }
        return i18n.global.t(mappedKey)
      }
      // Fall back to the base adapter for unmapped keys
      return baseAdapter.t(key, ...params)
    },
  }
}

const vuetify = createVuetify({
  locale: {
    adapter: createMappedVueI18nAdapter(),
  },
  components: {
    VSnackbarQueue,
  },
  icons: {
    defaultSet: 'mdi',
    aliases,
    sets: {
      mdi,
    },
  },
  // Component defaults cover what recurs across the app; they are deliberately
  // customer-ui's OWN set, not a copy of ada-app-ui's.
  defaults: {
    VTextField: {
      variant: 'outlined',
    },
    VSelect: {
      variant: 'outlined',
    },
    VAutocomplete: {
      variant: 'outlined',
    },
    VTextarea: {
      variant: 'outlined',
    },
    VSnackbar: {
      closable: true,
      color: 'primary',
    },
    VDateInput: {
      prependInnerIcon: mdiCalendarBlank,
      prependIcon: '',
      pickerProps: {
        firstDayOfWeek: 1,
        hideTitle: true,
        color: 'primary',
      },
    },
    VDatePicker: {
      firstDayOfWeek: 1,
      hideTitle: true,
      color: 'primary',
    },
  },
  theme: {
    // The theme keeps the pre-ADA-4410 name 'light' and full palette — the
    // frozen style_mixin resolves status colors via
    // $vuetify.theme.themes.light.colors['<color>_<context>'] and legacy
    // markup uses the *_dark/_main/_light families. ada-app-ui
    // palette adoption is deferred to the per-view migration (ADA-4372).
    defaultTheme: 'light',
    themes: {
      light: {
        dark: false,
        colors: {
          'background': '#FFFFFF',
          'on-background': '#0A0D0D',
          'surface': '#FFFFFF',
          'on-surface': '#0A0D0D',
          'primary': '#0A0D0D',
          'on-primary': '#FFFFFF',
          'secondary': '#EEEEEE',
          'on-secondary': '#0A0D0D',
          // Vuetify's standard surface-tint slot — the ticket design's unread-card
          // background (design/supoort_page_better_res.png); referenced via theme var,
          // never as a hex in components.
          'surface-light': '#FAFAFA',
          'success': '#1F4721',
          'on-success': '#ffffff',
          'warning': '#FB8C00',
          'on-warning': '#ffffff',
          'error': '#C20A20',
          'on-error': '#ffffff',

          'bankid': '#183E4F',
          'brand-primary': '#00E2E2',
          'brand-secondary': '#E1FCFF',
          'neutral': '#9D9E9E',

          // Status color families — resolved dynamically by the legacy
          // style_mixin (reference_color) and used as bg/text classes.
          'green_dark': '#006644',
          'green_main': '#00CC88',
          'green_light': '#E5F0EC',
          'grey_dark': '#4D4D4D',
          'grey_main': '#999999',
          'grey_light': '#EFEFEF',
          'blue_dark': '#0052CC',
          'blue_main': '#3385FF',
          'blue_light': '#E5EDFA',
          'indigo_dark': '#002B99',
          'indigo_main': '#336DFF',
          'indigo_light': '#A9BDEF',
          'orange_dark': '#8A4600',
          'orange_main': '#FF8A11',
          'orange_light': '#FFE6CC',
          'red_dark': '#D10B23',
          'red_main': '#F53D53',
          'red_light': '#FEE9EC',
          'pink_dark': '#8C0D0D',
          'pink_main': '#F05B5B',
          'pink_light': '#EDABAB',

          // Tonal-friendly status palette (ported from ada-app-ui's `statusColors`). Vuetify 4
          // derives tonal backgrounds by applying opacity to the base color, and the legacy
          // *_main tokens are too light for that to read well — these solid, higher-contrast
          // tokens render correct tonal chips/cards/buttons. Used by state/status UI (ADA-4430).
          'green': '#006611',
          'grey': '#1A1A1A',
          'blue': '#005CE5',
          'indigo': '#002B99',
          'orange': '#AD5800',
          'red': '#C20A20',
          'pink': '#910818',
          'yellow': '#80680A',
          'lightgrey': '#666666',
        },
        variables: {
          // Customer deviation from ada-app-ui (§2.7): the portal keeps its
          // existing DM Sans for BOTH headings and body — IBM Plex Serif
          // headings are intentionally not adopted.
          'font-heading': '"DM Sans Variable", sans-serif',
          'font-body': '"DM Sans Variable", sans-serif',
        },
      },
    },
  },
})

const ruleAliases = {
  min: (minValue: number, err?: string) => (v: unknown) =>
    typeof v === 'number' && v < minValue
      ? (err ?? i18n.global.t('rules.value_must_be_higher_than', [minValue]))
      : true,
  max: (maxValue: number, err?: string) => (v: unknown) =>
    typeof v === 'number' && v > maxValue ? (err ?? i18n.global.t('rules.value_must_be_lower_than', [maxValue])) : true,
}

declare module 'vuetify/labs/rules' {
  type CustomRuleAliases = typeof ruleAliases

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface RuleAliases extends CustomRuleAliases {}
}

export const rulesPlugin = createRulesPlugin(
  {
    aliases: ruleAliases,
  },
  vuetify.locale
)

export default vuetify
